import {
	ColumnDef,
	flexRender,
	getCoreRowModel,
	useReactTable,
	SortingState,
	getSortedRowModel,
} from '@tanstack/react-table'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import { useState, useEffect } from 'react'
import { Button } from '@/common/ui/button'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { ScrollArea, ScrollBar } from '@/common/ui/scroll-area'
import {
	DoubleArrowLeftIcon,
	DoubleArrowRightIcon,
} from '@radix-ui/react-icons'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { useTablePagination } from '@/common/hooks/useTablePaginate'
import { cn } from '@/common/lib/utils'
import { SortOrder } from '@/schema'

interface DataTableProps<TData, TValue> {
	columns: ColumnDef<TData, TValue>[]
	data: TData[]
	total: number
	isLoading?: boolean
	onSort?: (columnId: string, order: SortOrder | undefined) => void
	getRowClassName?: (row: TData) => string
}

export function DataTableArrow<TData, TValue>({
	columns,
	data,
	total,
	isLoading,
	onSort,
	getRowClassName,
}: DataTableProps<TData, TValue>) {
	const [sorting, setSorting] = useState<SortingState>([])
	const { pageSize, setPageSize, currentPage, setCurrentPage } =
		useTablePagination()

	useEffect(() => {
		if (sorting.length > 0 && onSort) {
			onSort(sorting[0].id, sorting[0].desc ? SortOrder.desc : SortOrder.asc)
		} else if (sorting.length === 0 && onSort) {
			onSort('', undefined)
		}
	}, [sorting, onSort])

	const table = useReactTable({
		data,
		columns,
		getCoreRowModel: getCoreRowModel(),
		manualPagination: true,
		pageCount: Math.ceil(total / pageSize),
		state: {
			sorting,
			pagination: {
				pageSize,
				pageIndex: currentPage - 1, // Convert to 0-based index for react-table
			},
		},
		onSortingChange: setSorting,
		getSortedRowModel: getSortedRowModel(),
		onPaginationChange: (updater) => {
			if (typeof updater === 'function') {
				const newState = updater({
					pageIndex: currentPage - 1,
					pageSize,
				})
				setCurrentPage(newState.pageIndex + 1)
				setPageSize(newState.pageSize)
			} else {
				setCurrentPage(updater.pageIndex + 1)
				setPageSize(updater.pageSize)
			}
		},
	})

	return (
		<div className="space-y-4">
			<ScrollArea className="h-[calc(80dvh-220px)] rounded-md border md:h-[calc(90dvh-240px)]">
				<div className="min-w-max">
					<Table className="relative">
						<TableHeader>
							{table.getHeaderGroups().map((headerGroup) => (
								<TableRow key={headerGroup.id}>
									{headerGroup.headers.map((header, index) => (
										<TableHead key={header.id + index} colSpan={header.colSpan}>
											{header.isPlaceholder
												? null
												: flexRender(
														header.column.columnDef.header,
														header.getContext(),
													)}
										</TableHead>
									))}
								</TableRow>
							))}
						</TableHeader>
						<TableBody>
							{isLoading ? (
								<TableRow>
									<TableCell
										colSpan={columns.length}
										className="h-24 text-center"
									>
										กำลังโหลด...
									</TableCell>
								</TableRow>
							) : table.getRowModel().rows?.length ? (
								table.getRowModel().rows.map((row) => (
									<TableRow
										key={row.id}
										data-state={row.getIsSelected() && 'selected'}
										className={cn('h-16', getRowClassName?.(row.original))}
									>
										{row.getVisibleCells().map((cell, index) => (
											<TableCell key={cell.id + index}>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext(),
												)}
											</TableCell>
										))}
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell
										colSpan={columns.length}
										className="h-24 text-center"
									>
										ไม่พบข้อมูล
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
				<ScrollBar orientation="horizontal" />
			</ScrollArea>

			<div className="flex flex-col items-center justify-end gap-2 space-x-2 py-4 sm:flex-row">
				<div className="flex w-full items-center justify-between">
					<div className="flex-1 text-sm text-muted-foreground">
						{total > 0 ? (
							<>
								Showing {(currentPage - 1) * pageSize + 1} to{' '}
								{Math.min(currentPage * pageSize, total)} of {total} entries
							</>
						) : (
							'No entries found'
						)}
					</div>
					<div className="flex flex-col items-center gap-4 sm:flex-row sm:gap-6 lg:gap-8">
						<div className="flex items-center space-x-2">
							<p className="whitespace-nowrap text-sm font-medium">
								Rows per page
							</p>
							<Select
								value={`${pageSize}`}
								onValueChange={(value) => {
									table.setPageSize(Number(value))
								}}
							>
								<SelectTrigger className="h-8 w-[70px]">
									<SelectValue placeholder={pageSize} />
								</SelectTrigger>
								<SelectContent side="top">
									{[10, 20, 30, 40, 50].map((size) => (
										<SelectItem key={size} value={`${size}`}>
											{size}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
				</div>
				<div className="flex w-full items-center justify-between gap-2 sm:justify-end">
					<div className="flex w-[150px] items-center justify-center text-sm font-medium">
						{total > 0 ? (
							<>
								Page {currentPage} of {table.getPageCount()}
							</>
						) : (
							'No pages'
						)}
					</div>
					<div className="flex items-center space-x-2">
						<Button
							aria-label="Go to first page"
							variant="outline"
							className="hidden h-8 w-8 p-0 lg:flex"
							onClick={() => table.setPageIndex(0)}
							disabled={!table.getCanPreviousPage() || isLoading}
						>
							<DoubleArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
						</Button>
						<Button
							aria-label="Go to previous page"
							variant="outline"
							className="h-8 w-8 p-0"
							onClick={() => table.previousPage()}
							disabled={!table.getCanPreviousPage() || isLoading}
						>
							<ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
						</Button>
						<Button
							aria-label="Go to next page"
							variant="outline"
							className="h-8 w-8 p-0"
							onClick={() => table.nextPage()}
							disabled={!table.getCanNextPage() || isLoading}
						>
							<ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
						</Button>
						<Button
							aria-label="Go to last page"
							variant="outline"
							className="hidden h-8 w-8 p-0 lg:flex"
							onClick={() => table.setPageIndex(table.getPageCount() - 1)}
							disabled={!table.getCanNextPage() || isLoading}
						>
							<DoubleArrowRightIcon className="h-4 w-4" aria-hidden="true" />
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}
